import { createElement } from 'react';
import type { ReactNode } from 'react';
import { Provider } from 'react-redux';
import { store } from './Redux/store';
import type { ResolvedCampaign } from './compactCampaignLanding/campaignRegistry';
import { loadMainApp } from './fastRouteLoader';
import { abTestingManager } from './AbTesting/AbTestingManager';
import { DecisionType } from './AbTesting/AbTestingTypes';
import { reportAppUsage } from './compactCampaignLanding/AppUsageReporter';

interface FastCampaignLoaderParams {
	render: (node: ReactNode) => void;
	resolvedCampaign: ResolvedCampaign | null;
	shouldRenderCampaignLanding: boolean;
}

/**
 * Fast path bootstrapper that prioritizes campaign landing experiences.
 * It eagerly renders the lightweight landing when possible and only loads
 * the full application bundle if no campaign landing is available or loading fails.
 */
export async function fastCampaignLoader({
	render,
	resolvedCampaign,
	shouldRenderCampaignLanding,
}: FastCampaignLoaderParams): Promise<void> {
	const camaignLoader = shouldRenderCampaignLanding ? resolvedCampaign?.loader : null;

	if (camaignLoader) {
		// Check for AB Testing Initial Route Override
		// We are inside a campaign flow (e.g. ?c=santa25)
		const variant = abTestingManager.getCurrentVariant();
		const initialRoute = variant?.decisionPoints.find(
			(dp) => dp.id === 'InitialRoute' && dp.type === DecisionType.String
		)?.stringValue;

		if (initialRoute) {
			// Manual tracking for redirect variants (like DirectSanta) since they skip the landing component
			reportAppUsage(
				"application.entry-point",
				{ 
                    campaignCode: resolvedCampaign!.code,
                    variantName: variant?.name,
                    customContext: { key: 'landing-page', value: initialRoute } 
                }
			);

			window.history.replaceState(null, '', initialRoute);
			await loadMainApp(render);
			return;
		}

		try {
			const { default: CampaignLanding } = await camaignLoader();
			reportAppUsage("application.entry-point", {
				campaignCode: resolvedCampaign?.code,
			});

			render(
				createElement(
					Provider,
					{ store, children: createElement(CampaignLanding) }
				)
			);
			return;
		} catch (error) {
			console.error('Failed to load campaign landing. Falling back to main app.', error);
		}
	}

	reportAppUsage("application.entry-point", {
		campaignCode: resolvedCampaign?.code,
	});

	await loadMainApp(render);
}
